// Utility functions to filter chargers based on selected filters

// Helper function to normalize socket/connector types to filter IDs
export const normalizeSocketType = (connectorType) => {
  if (!connectorType) return '';
  
  const type = String(connectorType).toLowerCase().trim();
  
  // Map actual connector types to filter IDs
  const socketMapping = {
    // Numeric IDs (add as needed for your data)
    '1': 'connector1',
    '2': 'connector2',
    '3': 'connector3',
    '4': 'connector4',
    '5': 'connector5',
    '6': 'connector6',
    '7': 'connector7',
    '9': 'connector2', // Type 2 (example, adjust as needed)
    '20': 'connector1', // CCS Type 2 (example, adjust as needed)
    // Add more numeric or string IDs as needed
    // CCS Type 2
    'ccs type 2': 'connector1',
    'ccs type2': 'connector1',
    'ccs2': 'connector1',
    'ccs type ii': 'connector1',
    'ccs': 'connector1',
    'combo': 'connector1',
    'combo 2': 'connector1',
    
    // Type 2
    'type 2': 'connector2',
    'type2': 'connector2',
    'type ii': 'connector2',
    'mennekes': 'connector2',
    'iec 62196-2': 'connector2',
    'iec62196-2': 'connector2',
    
    // CHAdeMO
    'chademo': 'connector3',
    'chade mo': 'connector3',
    'chade-mo': 'connector3',
    'chademo 1': 'connector3',
    'chademo 2': 'connector3',
    
    // Type 1
    'type 1': 'connector4',
    'type1': 'connector4',
    'type i': 'connector4',
    'j1772 type 1': 'connector4',
    'j1772 type1': 'connector4',
    'sae j1772': 'connector4',
    'saej1772': 'connector4',
    
    // Type 1 AC
    'type 1 ac': 'connector5',
    'type1 ac': 'connector5',
    'type 1ac': 'connector5',
    'type1ac': 'connector5',
    'j1772 ac': 'connector5',
    
    // 3 PIN
    '3 pin': 'connector6',
    '3pin': 'connector6',
    'three pin': 'connector6',
    'threepin': 'connector6',
    'domestic': 'connector6',
    'domestic socket': 'connector6',
    'uk plug': 'connector6',
    'bs 1363': 'connector6',
    'bs1363': 'connector6',
    
    // GBT
    'gbt': 'connector7',
    'gbt ac': 'connector7',
    'gbt dc': 'connector7',
    'guobiao': 'connector7',
    'guo biao': 'connector7',
    
    // CCS Type 1
    'ccs type 1': 'ccstype1',
    'ccs type1': 'ccstype1',
    'ccs1': 'ccstype1',
    'ccs type i': 'ccstype1',
    
    // CCS/SAE
    'ccs/sae': 'ccssae',
    'ccs sae': 'ccssae',
    'combo 1': 'ccssae',
    'combo1': 'ccssae',
    
    // Three Phase
    'three phase': 'threephase',
    'threephase': 'threephase',
    '3 phase': 'threephase',
    '3phase': 'threephase',
    'three-phase': 'threephase',
    '3-phase': 'threephase',
    
    // Wall
    'wall': 'wall',
    'wall socket': 'wall',
    'wallbox': 'wall',
    'wall box': 'wall',
    'wall charger': 'wall',
    
    // J1772
    'j1772': 'j1772',
    'j 1772': 'j1772',
    
    // IEC60309
    'iec60309': 'ie60309',
    'iec 60309': 'ie60309',
    'iec-60309': 'ie60309',
    'industrial': 'ie60309',
    'industrial socket': 'ie60309',
    'cee': 'ie60309',
    'cee form': 'ie60309'
  };
  
  // Check for exact matches first
  if (socketMapping[type]) {
    return socketMapping[type];
  }
  
  // Check for partial matches
  for (const [fullType, filterId] of Object.entries(socketMapping)) {
    if (type.includes(fullType) || fullType.includes(type)) {
      return filterId;
    }
  }
  
  // If no match found, return the original type (cleaned)
  return type.replace(/\s+/g, '_').toLowerCase();
};

// Helper function to normalize network names to short names
export const normalizeNetworkName = (networkName) => {
  if (!networkName) return '';
  
  const name = String(networkName).toLowerCase().trim();
  
  // Map full names to short names
  const networkMapping = {
    'indian oil corporation limited': 'iocl',
    'indian oil': 'iocl',
    'iocl': 'iocl',
    'hindustan petroleum': 'hp',
    'hindustan petroleum corporation': 'hp',
    'hindustan petroleum corporation limited': 'hp',
    'hp': 'hp',
    'hpcl': 'hp',
    'hp charging': 'hp',
    'hp ev charging': 'hp',
    'hp petrol pump': 'hp',
    'hp petrol': 'hp',
    'hp fuel': 'hp',
    'hp station': 'hp',
    'hp gas station': 'hp',
    'hp fuel station': 'hp',
    'hp petrol station': 'hp',
    'hp charging station': 'hp',
    'hp ev station': 'hp',
    'hp electric': 'hp',
    'hp electric vehicle': 'hp',
    'hp ev': 'hp',
    'hp ev charging station': 'hp',
    'hp ev charging point': 'hp',
    'hp charging point': 'hp',
    'hp fast charging': 'hp',
    'hp fast charger': 'hp',
    'hp rapid charging': 'hp',
    'hp rapid charger': 'hp',
    'bharat petroleum': 'bpcl',
    'bharat petroleum corporation': 'bpcl',
    'bharat petroleum corporation limited': 'bpcl',
    'bpcl': 'bpcl',
    'bp charging': 'bpcl',
    'bp ev charging': 'bpcl',
    'bp petrol pump': 'bpcl',
    'bp petrol': 'bpcl',
    'bp fuel': 'bpcl',
    'bp station': 'bpcl',
    'bp gas station': 'bpcl',
    'bp fuel station': 'bpcl',
    'bp petrol station': 'bpcl',
    'bp charging station': 'bpcl',
    'bp ev station': 'bpcl',
    'bp electric': 'bpcl',
    'bp electric vehicle': 'bpcl',
    'bp ev': 'bpcl',
    'bp ev charging station': 'bpcl',
    'bp ev charging point': 'bpcl',
    'bp charging point': 'bpcl',
    'bp fast charging': 'bpcl',
    'bp fast charger': 'bpcl',
    'bp rapid charging': 'bpcl',
    'bp rapid charger': 'bpcl',
    'tata power': 'tata',
    'tata power ez charge': 'tata',
    'tata power fc': 'tata',
    'tata power fast charge': 'tata',
    'tata power charging': 'tata',
    'tatapower': 'tata',
    'tata': 'tata',
    'ather': 'ather',
    'ather energy': 'ather',
    'ather grid': 'ather',
    'jio bp': 'jio_bp',
    'jio-bp': 'jio_bp',
    'jio bp pulse': 'jio_bp',
    'jio-bp pulse': 'jio_bp',
    'jiobp': 'jio_bp',
    'jio': 'jio_bp',
    'pulse': 'jio_bp',
    'volttic': 'volttic',
    'zeon': 'zeon',
    'zeon energy': 'zeon',
    'relux': 'relux',
    'relux energy': 'relux',
    'statiq': 'statiq',
    'statiq energy': 'statiq',
    'tcil': 'tcil',
    'telecommunications': 'tcil',
    'reil': 'reil',
    'rural electrification': 'reil',
    'plugzmart': 'plugzmart',
    'plugz mart': 'plugzmart',
    'pgcil': 'pgcil',
    'power grid': 'pgcil',
    'ola': 'ola',
    'ola electric': 'ola',
    'oem': 'oem',
    'original equipment': 'oem',
    'ntpc': 'ntpc',
    'national thermal': 'ntpc',
    'minooki': 'minooki',
    'minooki energy': 'minooki',
    'kropex': 'kropex',
    'kropex energy': 'kropex',
    'kazam': 'kazam',
    'kazam energy': 'kazam',
    'kht': 'kht',
    'kht energy': 'kht',
    'instavolt': 'instavolt',
    'insta volt': 'instavolt',
    'goego': 'goego',
    'go ego': 'goego',
    'generic': 'generic',
    'general': 'generic',
    'fortum': 'fortum',
    'fortum energy': 'fortum',
    'electriva': 'electriva',
    'electriva energy': 'electriva',
    'verde': 'verde',
    'verde mobility': 'verde',
    'aargo': 'aargo',
    'aargo energy': 'aargo',
    'bbmp': 'bbmp',
    'bruhat bengaluru': 'bbmp',
    'bruhat bengaluru mahanagara': 'bbmp',
    'bengaluru': 'bbmp',
    'bangalore': 'bbmp',
    'xobolt': 'xobolt',
    'xo bolt': 'xobolt',
    'xo-bolt': 'xobolt',
    // Add Zynka mapping
    'zynka': 'zynka',
    'zynka energy': 'zynka',
    'zynka charging': 'zynka',
    'zynka charge': 'zynka',
    // Add BMW/RechargeEV mapping
    'bmw': 'bmw',
    'bmw group': 'bmw',
    'bmw navnit': 'bmw',
    'bmw navnit motors': 'bmw',
    'rechargeev': 'bmw',
    'rechargev': 'bmw',
    'bmw navnit motors-rechargeev': 'bmw',
    'bmw navnit motors rechargeev': 'bmw',
    'navnit motors': 'bmw',
    'navnit': 'bmw',
    // Add compound IOCL mappings
    'iocl powergrid': 'iocl',
    'iocl powergrid electric vehicle charging station': 'iocl',
    'iocl powergrid electric vechicle charging station': 'iocl',
    'powergrid electric vehicle': 'iocl',
    'powergrid electric vechicle': 'iocl',
    'indian oil corporation powergrid': 'iocl',
    'indian oil powergrid': 'iocl'
  };
  
  // Check for exact matches first
  if (networkMapping[name]) {
    return networkMapping[name];
  }
  
  // Special case handling (order matters)
  
  // 1. Zynka - check first
  if (name.includes('zynka')) {
    return 'zynka';
  }
  
  // 2. IOCL and Powergrid combinations - handle compound names
  if (name.includes('iocl') || 
      name.includes('iocl pump') ||
      name.includes('iocl station') ||
      name.includes('iocl powergrid') ||
      (name.includes('indian oil') && (name.includes('corporation') || name.includes('powergrid'))) ||
      (name.includes('powergrid') && name.includes('electric') && (name.includes('vehicle') || name.includes('vechicle'))) ||
      name.includes('indian oil corporation')) {
    return 'iocl';
  }
  
  // 3. BMW and RechargeEV combinations
  if (name.includes('bmw') || name.includes('rechargeev') || name.includes('rechargev')) {
    return 'bmw';
  }
  
  // 4. Tata Power variations - check before general 'tata'
  if (name.includes('tata power') || 
      name.includes('tatapower') || 
      name.includes('tata power fc') ||
      name.includes('tata power fast charge') ||
      name.includes('tata power ez') ||
      (name.includes('tata') && name.includes('fc')) ||
      name.includes('tml') && name.includes('tata')) {
    return 'tata';
  }
  
  // 5. Jio BP variations - check before individual 'jio' or 'bp'
  if ((name.includes('jio') && name.includes('bp')) || 
      name.includes('jio-bp') || 
      name.includes('jiobp') ||
      name.includes('pulse') ||
      (name.includes('dmrc') && name.includes('jio')) ||
      (name.includes('jio') && name.includes('pulse'))) {
    return 'jio_bp';
  }
  
  // 6. HP - check before other partial matches
  if (name.includes('hp') || name.includes('hindustan')) {
    return 'hp';
  }
  
  // 7. BPCL - check before BP to avoid conflicts
  if (name.includes('bpcl') || (name.includes('bharat') && name.includes('petroleum'))) {
    return 'bpcl';
  }
  
  // 8. BP (only if not already matched above)
  if (name.includes('bp') && !name.includes('bharat') && !name.includes('bpcl') && !name.includes('jio')) {
    return 'jio_bp'; // Assume standalone BP is Jio BP
  }
  
  // 9. Check for other partial matches
  const keywordMatches = {
    'tata': 'tata',
    'bmw': 'bmw',
    'navnit': 'bmw',
    'rechargeev': 'bmw',
    'rechargev': 'bmw',
    'ather': 'ather',
    'volttic': 'volttic',
    'zeon': 'zeon',
    'relux': 'relux',
    'statiq': 'statiq',
    'tcil': 'tcil',
    'reil': 'reil',
    'plugzmart': 'plugzmart',
    'plugz': 'plugzmart',
    'pgcil': 'pgcil',
    'ola': 'ola',
    'oem': 'oem',
    'ntpc': 'ntpc',
    'minooki': 'minooki',
    'kropex': 'kropex',
    'kazam': 'kazam',
    'kht': 'kht',
    'instavolt': 'instavolt',
    'goego': 'goego',
    'fortum': 'fortum',
    'electriva': 'electriva',
    'verde': 'verde',
    'aargo': 'aargo',
    'bbmp': 'bbmp',
    'bengaluru': 'bbmp',
    'bangalore': 'bbmp',
    'xobolt': 'xobolt',
    'bijlify': 'bijlify',
    'bijli': 'bijlify',
    'powergrid': 'iocl',
    'indian oil': 'iocl'
  };
  
  for (const [keyword, network] of Object.entries(keywordMatches)) {
    if (name.includes(keyword)) {
      return network;
    }
  }
  
  // If no match found, return 'other' for unrecognized networks
  return 'other';
};

export const filterChargers = (chargers, filters) => {
  if (!chargers || !filters) return chargers;
  
  // If no filters are active, return all chargers
  if (!hasActiveFilters(filters)) {
    return chargers;
  }

  // Helper to robustly extract numeric kW from any value or charger name (copied from getSpeedsFromChargers)
  const extractKW = (val, chargerObj) => {
    if (typeof val === 'number' && val > 0) return val;
    if (typeof val === 'string') {
      const match = val.match(/([0-9]+\.?[0-9]*)/);
      if (match) return parseFloat(match[1]);
    }
    if (chargerObj && typeof chargerObj.name === 'string') {
      const nameMatch = chargerObj.name.match(/([0-9]+\.?[0-9]*)\s*(kW|kw|KW)/);
      if (nameMatch) return parseFloat(nameMatch[1]);
    }
    return 0;
  };
  const filtered = chargers.map(charger => {
    let matchingConnectors = [];
    let hasMatchingSpeed = false;

    // Helper to check if a power value matches any selected speed
    const matchesSpeed = (power) => {
      return filters.speeds.some(speedFilter => {
        switch (speedFilter) {
          case 'power1': return power < 7;
          case 'power2': return power >= 7 && power <= 22;
          case 'power3': return power > 22 && power <= 50;
          case 'power4': return power > 50;
          default: return false;
        }
      });
    };

    // Check devices[].stations[] structure for power (preferred)
    if (charger.devices && Array.isArray(charger.devices)) {
      charger.devices.forEach(device => {
        if (device.stations && Array.isArray(device.stations)) {
          device.stations.forEach(station => {
            const power = extractKW(station.power || station.kw || station.speed || station.capacity || station.wattage || '', charger);
            if (power > 0 && matchesSpeed(power)) {
              hasMatchingSpeed = true;
              matchingConnectors.push({
                ...station,
                __from: 'station',
                __device: device,
                __charger: charger
              });
            }
          });
        }
      });
    }

    // Also check legacy connector structure for power
    const legacyConnectors = [
      charger.connectors,
      charger.chargingPorts,
      charger.stations,
      charger.plugTypes,
      charger.socketTypes,
      charger.ports
    ].filter(Boolean);
    legacyConnectors.forEach(connectorArray => {
      if (Array.isArray(connectorArray)) {
        connectorArray.forEach((connector) => {
          const power = extractKW(connector.power || connector.kw || connector.speed || connector.capacity || '', charger);
          if (power > 0 && matchesSpeed(power)) {
            hasMatchingSpeed = true;
            matchingConnectors.push({
              ...connector,
              __from: 'connector',
              __charger: charger
            });
          }
        });
      }
    });

    // Check charger-level power (if no stations/connectors matched)
    if (!hasMatchingSpeed) {
      const chargerPower = extractKW(charger.power || charger.speed || charger.kw || charger.maxPower || charger.capacity || '', charger);
      if (chargerPower > 0 && matchesSpeed(chargerPower)) {
        hasMatchingSpeed = true;
        matchingConnectors.push({
          power: chargerPower,
          __from: 'charger',
          __charger: charger
        });
      }
    }

    // If no power data found, default to slow charging category
    if (!hasMatchingSpeed && filters.speeds.includes('power1')) {
      hasMatchingSpeed = true;
      // If no connectors matched, add all connectors (for slow/unknown)
      // Prefer devices[].stations, else legacy connectors
      let added = false;
      if (charger.devices && Array.isArray(charger.devices)) {
        charger.devices.forEach(device => {
          if (device.stations && Array.isArray(device.stations)) {
            device.stations.forEach(station => {
              matchingConnectors.push({
                ...station,
                __from: 'station',
                __device: device,
                __charger: charger
              });
              added = true;
            });
          }
        });
      }
      if (!added) {
        const legacyConnectors = [
          charger.connectors,
          charger.chargingPorts,
          charger.stations,
          charger.plugTypes,
          charger.socketTypes,
          charger.ports
        ].filter(Boolean);
        legacyConnectors.forEach(connectorArray => {
          if (Array.isArray(connectorArray)) {
            connectorArray.forEach((connector) => {
              matchingConnectors.push({
                ...connector,
                __from: 'connector',
                __charger: charger
              });
            });
          }
        });
      }
    }

    // If no match, skip this charger
    if (filters.speeds && filters.speeds.length > 0 && !hasMatchingSpeed) {
      return null;
    }

    // ...existing code for network filtering...
    // Filter by networks using normalized short names
    if (filters.networks && Array.isArray(filters.networks) && filters.networks.length > 0) {
      const possibleNetworkFields = [
        charger.network,
        charger.operator,
        charger.chargepoint_name,
        charger.name,
        charger.provider,
        charger.owner
      ].filter(Boolean);
      const normalizedChargerNetworks = possibleNetworkFields.map(normalizeNetworkName);
      const hasMatchingNetwork = filters.networks.some(filterNetwork => {
        if (!filterNetwork) return false;
        const normalizedFilter = filterNetwork.toLowerCase();
        if (normalizedFilter === 'other') {
          return normalizedChargerNetworks.includes('other');
        }
        return normalizedChargerNetworks.some(chargerNetwork => 
          chargerNetwork === normalizedFilter || 
          chargerNetwork.includes(normalizedFilter) || 
          normalizedFilter.includes(chargerNetwork)
        );
      });
      if (!hasMatchingNetwork) return null;
    }

    // Attach matchingConnectors for UI use
    return { ...charger, matchingConnectors };
  }).filter(Boolean);

  return filtered;
}

export const getFilteredChargersCount = (chargers, filters) => {
  const filteredChargers = filterChargers(chargers, filters);
  return filteredChargers.length;
};

export const hasActiveFilters = (filters) => {
  if (!filters) return false;
  
  return Object.values(filters).some(filterArray => 
    Array.isArray(filterArray) && filterArray.length > 0
  );
};

// Helper function to get charger statistics
export const getChargerStats = (chargers, filters) => {
  const filteredChargers = filterChargers(chargers, filters);
  
  // Extract unique networks from charger data
  const allNetworks = [...new Set(chargers.flatMap(c => {
    const networks = [
      c.network,
      c.operator,
      c.chargepoint_name,
      c.name,
      c.provider,
      c.owner
    ].filter(Boolean);
    return networks;
  }))];
  
  const stats = {
    total: chargers.length,
    filtered: filteredChargers.length,
    available: filteredChargers.filter(c => c.availability === 'Available').length,
    unavailable: filteredChargers.filter(c => c.availability !== 'Available').length,
    networks: allNetworks,
    socketTypes: [...new Set(chargers.flatMap(c => 
      c.connectors?.map(conn => conn.type || conn.connectorType).filter(Boolean) || []
    ))]
  };
  
  return stats;
};

// Helper function to extract network names from charger data
export const extractNetworksFromChargers = (chargers) => {
  const networkMap = new Map();
  
  chargers.forEach(charger => {
    const possibleNetworks = [
      charger.network,
      charger.operator,
      charger.chargepoint_name,
      charger.name,
      charger.provider,
      charger.owner
    ].filter(Boolean);
    
    possibleNetworks.forEach(network => {
      if (!networkMap.has(network)) {
        networkMap.set(network, 0);
      }
      networkMap.set(network, networkMap.get(network) + 1);
    });
  });
  
  return Array.from(networkMap.entries())
    .sort((a, b) => b[1] - a[1]) // Sort by count descending
    .map(([network, count]) => ({ network, count }));
};

// Returns array of unique socket types with counts and icon paths (fully dynamic)
export const getSocketTypesFromChargers = (chargers = []) => {
  if (!chargers || chargers.length === 0) {
    return [];
  }
  
  const typeMap = new Map();
  chargers.forEach((charger, ) => {
    // Extract connectors from devices[].stations[].connector structure
    if (charger.devices && Array.isArray(charger.devices)) {
      charger.devices.forEach((device) => {
        if (device.stations && Array.isArray(device.stations)) {
          device.stations.forEach((station) => {
            const rawType = station.connector || '';
            if (rawType) {
              const type = normalizeSocketType(rawType);
              const label = rawType;
              
              // Map connector types to correct icon paths
              let icon = '/image/connectorSvg/img_connector_6.svg'; // default (3pin)
              if (type.includes('connector1') || type.includes('ccs')) icon = '/image/connectorSvg/img_connector_1.svg';
              else if (type.includes('connector2') || type.includes('type') || type.includes('mennekes')) icon = '/image/connectorSvg/img_connector_2.svg';
              else if (type.includes('connector3') || type.includes('chademo')) icon = '/image/connectorSvg/img_connector_3.svg';
              else if (type.includes('connector4') || type.includes('j1772')) icon = '/image/connectorSvg/img_connector_4.svg';
              else if (type.includes('connector6') || type.includes('3pin') || type.includes('domestic')) icon = '/image/connectorSvg/img_connector_6.svg';
              else if (type.includes('connector7') || type.includes('gbt')) icon = '/image/connectorSvg/img_connector_7.svg';
              else if (type.includes('threephase') || type.includes('3phase')) icon = '/image/connectorSvg/img_sockets_3_phase.svg';
              else if (type.includes('ccstype1')) icon = '/image/connectorSvg/img_sockets_CCS_type_1.svg';
              else if (type.includes('wall')) icon = '/image/connectorSvg/img_sockets_Wall.svg';
              else if (type.includes('ie60309') || type.includes('iec60309')) icon = '/image/connectorSvg/img_sockets_iec60309.svg';
              
              if (!typeMap.has(type)) {
                typeMap.set(type, { id: type, label, count: 0, icon });
              }
              typeMap.get(type).count++;
            } else {
              // Default connector if none specified
              const defaultType = 'connector6';
              const defaultLabel = '3 Pin';
              const defaultIcon = '/image/connectorSvg/img_connector_6.svg';
              
              if (!typeMap.has(defaultType)) {
                typeMap.set(defaultType, { id: defaultType, label: defaultLabel, count: 0, icon: defaultIcon });
              }
              typeMap.get(defaultType).count++;
            }
          });
        }
      });
    }
    
    // Also check legacy connector formats for backward compatibility
    const legacyConnectors = [
      charger.connectors,
      charger.chargingPorts,
      charger.stations,
      charger.plugTypes,
      charger.socketTypes,
      charger.ports
    ].filter(Boolean);
    
    legacyConnectors.forEach(connectorArray => {
      if (Array.isArray(connectorArray)) {
        connectorArray.forEach((connector) => {
          const rawType = connector.type || connector.connectorType || connector.name || connector.socketType || '';
          if (rawType) {
            const type = normalizeSocketType(rawType);
            const label = rawType;      
            let icon = '/image/connectorSvg/img_connector_6.svg';
            if (type.includes('connector1') || type.includes('ccs')) icon = '/image/connectorSvg/img_connector_1.svg';
            else if (type.includes('connector2') || type.includes('type') || type.includes('mennekes')) icon = '/image/connectorSvg/img_connector_2.svg';
            else if (type.includes('connector3') || type.includes('chademo')) icon = '/image/connectorSvg/img_connector_3.svg';
            else if (type.includes('connector4') || type.includes('j1772')) icon = '/image/connectorSvg/img_connector_4.svg';
            else if (type.includes('connector6') || type.includes('3pin') || type.includes('domestic')) icon = '/image/connectorSvg/img_connector_6.svg';
            else if (type.includes('connector7') || type.includes('gbt')) icon = '/image/connectorSvg/img_connector_7.svg';
            
            if (!typeMap.has(type)) {
              typeMap.set(type, { id: type, label, count: 0, icon });
            }
            typeMap.get(type).count++;
          }
        });
      }
    });
  });
  
  const result = Array.from(typeMap.values());
  return result;
};

// Returns array of unique speed categories with counts and icon paths (fully dynamic)
export const getSpeedsFromChargers = (chargers = []) => {
  // DEBUG: Log all possible power fields for the first 5 chargers
  chargers.slice(0, 5).forEach(() => {
  });
  if (!chargers || chargers.length === 0) {
    return [];
  }
  
  const speedCategories = [
    { id: 'power1', label: 'Slow (<7kW)', min: 0, max: 7, icon: '/image/speed_slow.png', color: 'text-blue-500' },
    { id: 'power2', label: 'Fast (7-22kW)', min: 7, max: 22, icon: '/image/speed_fast.png', color: 'text-green-500' },
    { id: 'power3', label: 'Rapid (22-50kW)', min: 22, max: 50, icon: '/image/speed_rapid.png', color: 'text-yellow-500' },
    { id: 'power4', label: 'Ultra Rapid (>50kW)', min: 50, max: Infinity, icon: '/image/speed_ultra_rapid.png', color: 'text-red-500' },
  ];
  const counts = [0, 0, 0, 0];
  
  // Helper to robustly extract numeric kW from any value or charger name
  const extractKW = (val, chargerObj) => {
    if (typeof val === 'number' && val > 0) return val;
    if (typeof val === 'string') {
      // Extract first number in string (handles "22 kW", "7.4kw", etc)
      const match = val.match(/([0-9]+\.?[0-9]*)/);
      if (match) return parseFloat(match[1]);
    }
    // Fallback: try to extract from charger name if present
    if (chargerObj && typeof chargerObj.name === 'string') {
      const nameMatch = chargerObj.name.match(/([0-9]+\.?[0-9]*)\s*(kW|kw|KW)/);
      if (nameMatch) return parseFloat(nameMatch[1]);
    }
    return 0;
  };

  chargers.forEach((charger) => {
    const chargerCategories = new Set();
    let foundPower = false;

    // Check charger-level power first
    const chargerPower = extractKW(charger.power || charger.speed || charger.kw || charger.maxPower || charger.capacity || '', charger);
    if (chargerPower > 0) {
      foundPower = true;
      speedCategories.forEach((cat, idx) => {
        if (chargerPower >= cat.min && chargerPower < cat.max) {
          chargerCategories.add(idx);
        }
      });
    }

    // Extract power from devices[].stations[] structure
    if (charger.devices && Array.isArray(charger.devices)) {
      charger.devices.forEach((device) => {
        if (device.stations && Array.isArray(device.stations)) {
          device.stations.forEach((station) => {
            const power = extractKW(station.power || station.kw || station.speed || station.capacity || station.wattage || '', charger);
            if (power > 0) {
              foundPower = true;
              speedCategories.forEach((cat, idx) => {
                if (power >= cat.min && power < cat.max) {
                  chargerCategories.add(idx);
                }
              });
            }
          });
        }
      });
    }

    // Also check legacy connector-level power for backward compatibility
    const legacyConnectors = [
      charger.connectors,
      charger.chargingPorts,
      charger.stations,
      charger.plugTypes,
      charger.socketTypes,
      charger.ports
    ].filter(Boolean);

    legacyConnectors.forEach(connectorArray => {
      if (Array.isArray(connectorArray)) {
        connectorArray.forEach((connector) => {
          const power = extractKW(connector.power || connector.kw || connector.speed || connector.capacity || '', charger);
          if (power > 0) {
            foundPower = true;
            speedCategories.forEach((cat, idx) => {
              if (power >= cat.min && power < cat.max) {
                chargerCategories.add(idx);
              }
            });
          }
        });
      }
    });

    // If no power info found at all, count as slow (power1)
    if (!foundPower) {
      chargerCategories.add(0); // 0 index is power1 (slow)
    }

    chargerCategories.forEach(idx => {
      counts[idx]++;
    });
  });
  
  const result = speedCategories.map((cat, idx) => ({ ...cat, count: counts[idx] })).filter(cat => cat.count > 0);
  return result;
};

// Helper function to get network icon path
const getNetworkIconPath = (networkId) => {
  const iconMapping = {
    'iocl': 'IOCL',
    'hp': 'HP',
    'bpcl': 'BPCL', 
    'tata': 'Tata',
    'ather': 'Ather',
    'jio_bp': 'JioBP',
    'volttic': 'Volttic',
    'zeon': 'Zeon',
    'relux': 'Relux',
    'statiq': 'Statiq',
    'tcil': 'TCIL',
    'reil': 'REIL',
    'plugzmart': 'PlugzMart',
    'pgcil': 'PGCIL',
    'ola': 'Ola',
    'oem': 'OEM',
    'ntpc': 'NTPC',
    'minooki': 'Minooki',
    'kropex': 'Kropex',
    'kazam': 'Kazam',
    'kht': 'KHT',
    'instavolt': 'InstaVolt',
    'goego': 'Goego',
    'generic': 'Generic',
    'fortum': 'Fortum',
    'electriva': 'Electriva',
    'verde': 'verde',
    'aargo': 'AARgo',
    'bbmp': 'BBMP',
    'xobolt': 'XObolt',
    'other': 'Generic'
  };
  
  // Use mapped icon name or fallback to Generic for unknown networks
  const iconName = iconMapping[networkId] || 'Generic';
  return `/image/networkFilter/${iconName}.svg`;
};

// Returns array of unique networks with counts and icon paths (fully dynamic)
export const getNetworksFromChargers = (chargers = []) => {
  if (!chargers || chargers.length === 0) {
    return [];
  }
  
  const networkMap = new Map();
  
  chargers.forEach((charger) => {
    // Get the best network name for this charger (only one per charger)
    const possibleNetworks = [
      charger.network,
      charger.operator,
      charger.chargepoint_name,
      charger.name,
      charger.provider,
      charger.owner
    ].filter(Boolean);
    
    // Find the first valid network name and normalize it
    let bestNetwork = 'other';
    let originalNetworkName = 'Other';
    
    for (const network of possibleNetworks) {
      const norm = normalizeNetworkName(network);
      if (norm && norm !== 'other' && norm !== 'generic') {
        bestNetwork = norm;
        originalNetworkName = network;
        break;
      }
    }
    
    // If no valid network found, use 'other'
    const finalNorm = bestNetwork === 'other' ? 'other' : bestNetwork;
    const icon = getNetworkIconPath(finalNorm);
    
    // Create short display name
    const shortName = finalNorm === 'other' ? 'Other' : finalNorm.toUpperCase();
    
    if (!networkMap.has(finalNorm)) {
      networkMap.set(finalNorm, { 
        id: finalNorm, 
        label: shortName,
        fullName: originalNetworkName,
        count: 0, 
        icon 
      });
    }
    
    // Count each charger only once
    networkMap.get(finalNorm).count++;
  });
  
  return Array.from(networkMap.values());
}; 